/**
 * Resolving a cart line's price and label.
 *
 * A line may carry a variant, in which case the variant's price applies rather
 * than the product's own — the product's `price` is only a "from" summary once
 * variants exist, so charging it would undercharge every option above the
 * cheapest.
 *
 * Two shapes reach these helpers: server cart lines (populated `productId`,
 * `variantId`) and guest cart lines held in localStorage (which cache
 * `unitPrice` at add-to-cart time).
 */

export const lineUnitPrice = (item) => {
    if (typeof item?.unitPrice === 'number') return item.unitPrice; // guest cart

    const product = item?.productId;
    if (item?.variantId && product?.variants?.length) {
        const variant = product.variants.find(v => String(v._id) === String(item.variantId));
        if (variant) return variant.price;
    }
    return product?.price || 0;
};

export const lineVariantLabel = (item) => {
    if (item?.variantLabel) return item.variantLabel;

    const product = item?.productId;
    if (item?.variantId && product?.variants?.length) {
        return product.variants.find(v => String(v._id) === String(item.variantId))?.label || '';
    }
    return '';
};

export const lineTotal = (item) => lineUnitPrice(item) * (item?.quantity || 0);

/**
 * Collapses cart lines into one entry per product, keeping each variant as a
 * row beneath it. Two sizes of the same tape belong in one card, not two.
 * Insertion order is preserved so the cart does not reshuffle as items change.
 */
export const groupCartLines = (items = []) => {
    const groups = new Map();

    for (const item of items) {
        const product = item?.productId;
        const id = String(product?._id || product || '');
        if (!groups.has(id)) {
            groups.set(id, { key: id, product, lines: [], subtotal: 0, quantity: 0 });
        }
        const group = groups.get(id);
        group.lines.push(item);
        group.subtotal += lineTotal(item);
        group.quantity += item?.quantity || 0;
    }

    return [...groups.values()];
};
